import React, { useMemo } from 'react';
import { Card } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { BarChart } from 'echarts/charts';
import { GridComponent, TooltipComponent, LegendComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';

echarts.use([BarChart, GridComponent, TooltipComponent, LegendComponent, CanvasRenderer]);

const formatValue = (val, unit) => {
  if (!val) return '0';
  if (unit?.includes('CP') || unit === 'CPA' || unit === 'CPM' || unit === 'CPV') {
    return '$' + val.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  if (val >= 1000000) return (val / 1000000).toFixed(1) + 'M';
  if (val >= 1000) return (val / 1000).toFixed(1) + 'K';
  return val.toLocaleString();
};

const ObjectivesVsRealChart = ({ objectives = [] }) => {
  const options = useMemo(() => {
    if (!objectives.length) return {};

    const labels = objectives.map(o =>
      (o.clientName || '').length > 15
        ? (o.clientName || '').slice(0, 14) + '…'
        : o.clientName || 'N/A'
    );

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params) => {
          const idx = params[0].dataIndex;
          const obj = objectives[idx];
          const unit = obj?.unit || '';
          let html = `<strong>${obj?.clientName || ''}</strong><br/><small>${obj?.accountName || ''} · ${unit}</small><br/>`;
          params.forEach((p) => {
            html += `${p.marker} ${p.seriesName}: <strong>${formatValue(p.value, unit)}</strong><br/>`;
          });
          html += `<br/>Progreso: <strong>${(obj?.progressPct || 0).toFixed(1)}%</strong>`;
          return html;
        },
      },
      legend: {
        data: ['Objetivo', 'Esperado', 'Real'],
        bottom: 0,
        textStyle: { fontSize: 11 },
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '15%',
        top: '8%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: labels,
        axisLabel: { fontSize: 10, rotate: objectives.length > 4 ? 25 : 0 },
        axisLine: { lineStyle: { color: '#e0e0e0' } },
      },
      yAxis: {
        type: 'value',
        axisLine: { show: false },
        splitLine: { lineStyle: { color: '#f0f0f0' } },
        axisLabel: {
          fontSize: 10,
          color: '#666',
          formatter: (v) =>
            v >= 1000000 ? (v / 1000000).toFixed(1) + 'M' : v >= 1000 ? (v / 1000).toFixed(0) + 'K' : v,
        },
      },
      series: [
        {
          name: 'Objetivo',
          type: 'bar',
          data: objectives.map((o) => o.nominalValue || 0),
          barWidth: '20%',
          itemStyle: { color: '#e0e7ff', borderRadius: [4, 4, 0, 0] },
        },
        {
          name: 'Esperado',
          type: 'bar',
          data: objectives.map((o) => o.expectedValue || 0),
          barWidth: '20%',
          itemStyle: { color: '#93c5fd', borderRadius: [4, 4, 0, 0] },
        },
        {
          name: 'Real',
          type: 'bar',
          data: objectives.map((o) => o.realValue || 0),
          barWidth: '20%',
          itemStyle: {
            borderRadius: [4, 4, 0, 0],
            color: (params) => {
              const obj = objectives[params.dataIndex];
              const pct = obj?.progressPct || 0;
              if (pct >= 90 && pct <= 110) return '#00d97e';
              if (pct >= 70) return '#f5803e';
              return '#e63757';
            },
          },
        },
      ],
    };
  }, [objectives]);

  if (!objectives.length) {
    return (
      <Card className="shadow-sm border-0 h-100">
        <Card.Header className="bg-white border-bottom py-2">
          <h6 className="mb-0">
            <FontAwesomeIcon icon="chart-bar" className="me-2 text-primary" />
            Objetivo vs Real
          </h6>
        </Card.Header>
        <Card.Body className="text-center py-5">
          <FontAwesomeIcon icon="chart-bar" size="2x" className="text-300 mb-2" />
          <p className="text-muted mb-0">Sin datos de objetivos</p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="shadow-sm border-0 h-100">
      <Card.Header className="bg-white border-bottom py-2">
        <h6 className="mb-0">
          <FontAwesomeIcon icon="chart-bar" className="me-2 text-primary" />
          Objetivo vs Esperado vs Real
        </h6>
      </Card.Header>
      <Card.Body>
        <ReactEChartsCore
          echarts={echarts}
          option={options}
          style={{ height: 320 }}
          opts={{ renderer: 'canvas' }}
        />
      </Card.Body>
    </Card>
  );
};

export default ObjectivesVsRealChart;
